import { useRef, useState } from "react";
import AudioFormFillControl from "../components/audio-form-fill-control";
import Header from "../components/header";

type Confidence = "low" | "medium" | "high";
type FieldStatus = "missing" | "review" | "ready";

type ExtractedField = {
  value: string;
  confidence: Confidence;
  status: FieldStatus;
  evidence: string;
  warnings: string[];
};

type ExtractionPayload = {
  schemaVersion: string;
  fields: Record<string, ExtractedField>;
  alerts: string[];
};

type TranscriptionResponse = {
  success: boolean;
  transcriptionId: string;
  fileName: string;
  durationSeconds: number | null;
  transcript: string;
  transcriptionLatencyMs: number;
  model: string;
  transcriptionParams: Record<string, unknown>;
  usageEstimate: UsageEstimate;
  rawMetadata: Record<string, unknown>;
};

type UsageEstimate = {
  durationSeconds: number | null;
  audioMinutes: number | null;
  estimatedNeurons: number | null;
  estimatedUnitCostUsd: number | null;
  estimatedPaidOverageUsdIfAboveFreeTier: number | null;
  costConfig: {
    usdPerAudioMinute: number;
    neuronsPerAudioMinute: number;
    freeNeuronsPerDay: number;
    usdPerThousandNeurons: number;
    currency: string;
    source: string;
  };
  note: string;
};

type UsageSummaryBucket = {
  requests: number;
  audioMinutes: number;
  estimatedNeurons: number;
  estimatedUnitCostUsd: number;
  successfulRequests: number;
  failedRequests: number;
  freeNeuronsPerDay?: number;
  estimatedFreeNeuronsRemaining?: number;
  estimatedPaidOverageNeurons?: number;
  estimatedPaidOverageUsd?: number;
};

type UsageResponse = {
  success: boolean;
  events: Array<{
    id: string;
    createdAt: string;
    fileName: string;
    durationSeconds: number | null;
    transcriptionLatencyMs: number;
    cloudflareStatusCode: number;
    cloudflareSuccess: boolean;
    transcriptCharacters: number;
    usageEstimate: UsageEstimate;
  }>;
  summary: {
    today: UsageSummaryBucket;
    allTime: UsageSummaryBucket;
    costConfig: UsageEstimate["costConfig"];
    note: string;
  };
};

type ExtractionResponse = {
  success: boolean;
  transcriptionId: string | null;
  transcript: string;
  schema: Record<string, unknown>;
  extraction: ExtractionPayload;
  validationErrors: string[];
};

type ReviewField = {
  key: string;
  label: string;
  placeholder: string;
  multiline?: boolean;
};

type RecordingGuideGroup = {
  title: string;
  fields: string[];
  example: string;
};

// Vazio/undefined em build = mesma origem (Worker serve front e /api/*).
// Em `npm start` cai no `wrangler dev` local (npm run poc:api).
const apiBaseUrl = process.env.REACT_APP_AUDIO_POC_API_URL
  ?? (process.env.NODE_ENV === "development" ? "http://localhost:8787" : "");

const reviewFields: ReviewField[] = [
  { key: "fullName", label: "Nome completo", placeholder: "Ex: Ana Paula Santos" },
  { key: "cpf", label: "CPF", placeholder: "000.000.000-00" },
  { key: "birthDate", label: "Nascimento", placeholder: "15/03/1998" },
  { key: "phone", label: "Telefone", placeholder: "11999999999" },
  { key: "email", label: "E-mail", placeholder: "nome@email.com" },
  { key: "gender", label: "Gênero", placeholder: "Feminino, Masculino, Prefiro não informar" },
  { key: "zipCode", label: "CEP", placeholder: "00000000" },
  { key: "addressStreet", label: "Rua", placeholder: "Rua Augusta" },
  { key: "addressNumber", label: "Numero", placeholder: "1200" },
  { key: "addressComplement", label: "Complemento", placeholder: "Apto 12" },
  { key: "addressNeighborhood", label: "Bairro", placeholder: "Bela Vista" },
  { key: "addressCity", label: "Cidade", placeholder: "Sao Paulo" },
  { key: "addressState", label: "Estado", placeholder: "SP" },
  { key: "desiredRoles", label: "Cargos desejados", placeholder: "Auxiliar administrativo, recepcionista" },
  { key: "hasDriverLicense", label: "Possui CNH", placeholder: "true/false" },
  { key: "driverLicenseCategory", label: "Categoria da CNH", placeholder: "A, B, C..." },
  { key: "hasMotorcycle", label: "Moto propria", placeholder: "true/false" },
  { key: "immediateStartAvailability", label: "Inicio imediato", placeholder: "true/false" },
  { key: "travelAvailability", label: "Disponibilidade para viajar", placeholder: "true/false" },
  { key: "observations", label: "Observacoes", placeholder: "Texto livre extraido do audio", multiline: true }
];

const recordingGuideGroups: RecordingGuideGroup[] = [
  {
    title: "Dados pessoais",
    fields: ["Nome completo", "CPF", "Nascimento", "Telefone", "E-mail", "Gênero"],
    example: "Meu nome é Ana Paula Santos, CPF 12345678900, nasci em 15 de março de 1998, meu celular é 11999999999, email ana ponto santos arroba gmail ponto com."
  },
  {
    title: "Endereço",
    fields: ["CEP", "Rua", "Número", "Complemento", "Bairro", "Cidade", "Estado"],
    example: "Meu CEP é 01311000, moro na Avenida Paulista, número 1200, complemento conjunto 45, bairro Bela Vista, cidade São Paulo, estado SP."
  },
  {
    title: "Objetivos e disponibilidade",
    fields: ["Cargos desejados", "CNH", "Categoria", "Moto própria", "Início imediato", "Viagem", "Observações"],
    example: "Busco vagas de auxiliar administrativo e recepcionista, tenho CNH categoria B, não tenho moto, posso começar imediatamente e tenho disponibilidade para viajar."
  }
];

function createInitialFormValues() {
  return reviewFields.reduce<Record<string, string>>((acc, field) => {
    acc[field.key] = "";
    return acc;
  }, {});
}

function getFieldClassName(field?: ExtractedField) {
  if (!field) return "audio-poc-field";
  return `audio-poc-field audio-poc-field-${field.status} audio-poc-confidence-${field.confidence}`;
}

function formatCurrency(value: number | null | undefined) {
  if (value === null || value === undefined || Number.isNaN(value)) return "-";
  return `$${value.toFixed(6)}`;
}

function formatNumber(value: number | null | undefined, fractionDigits = 2) {
  if (value === null || value === undefined || Number.isNaN(value)) return "-";
  return value.toFixed(fractionDigits);
}

export default function PocPreenchimentoAudio() {
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [durationSeconds, setDurationSeconds] = useState<number | null>(null);
  const [recording, setRecording] = useState(false);
  const [loadingTranscription, setLoadingTranscription] = useState(false);
  const [loadingExtraction, setLoadingExtraction] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [status, setStatus] = useState("Selecione ou grave um audio para iniciar a POC.");
  const [error, setError] = useState("");
  const [transcription, setTranscription] = useState<TranscriptionResponse | null>(null);
  const [extraction, setExtraction] = useState<ExtractionResponse | null>(null);
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [formValues, setFormValues] = useState<Record<string, string>>(createInitialFormValues());
  const [fieldExtractions, setFieldExtractions] = useState<Record<string, ExtractedField>>({});
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  function setSelectedAudio(file: File) {
    if (audioUrl) URL.revokeObjectURL(audioUrl);

    setAudioFile(file);
    setAudioUrl(URL.createObjectURL(file));
    setDurationSeconds(null);
    setTranscription(null);
    setExtraction(null);
    setFormValues(createInitialFormValues());
    setFieldExtractions({});
    setReviewed(false);
    setConfirmed(false);
    setError("");
    setStatus("Audio pronto para transcricao.");
  }

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file) setSelectedAudio(file);
  }

  async function startRecording() {
    setError("");

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError("Este navegador nao suporta gravacao de audio via MediaRecorder.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      streamRef.current = stream;
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        const file = new File([blob], `audio-poc-${Date.now()}.webm`, { type: blob.type });
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        recorderRef.current = null;
        setRecording(false);
        setSelectedAudio(file);
      };
      recorder.start();
      setRecording(true);
      setStatus("Gravando audio. Pare a gravacao quando terminar a fala.");
    } catch {
      setError("Nao consegui acessar o microfone. Verifique permissao e HTTPS no celular.");
    }
  }

  function stopRecording() {
    recorderRef.current?.stop();
    setStatus("Finalizando gravacao.");
  }

  async function transcribeAudio() {
    if (!audioFile) return;

    setLoadingTranscription(true);
    setLoadingExtraction(false);
    setError("");
    setTranscription(null);
    setExtraction(null);
    setReviewed(false);
    setConfirmed(false);
    setStatus("Enviando audio para transcricao no Cloudflare Workers AI.");

    const formData = new FormData();
    formData.append("audio", audioFile);
    if (durationSeconds !== null) formData.append("durationSeconds", String(durationSeconds));

    try {
      const response = await fetch(`${apiBaseUrl}/api/transcribe`, {
        method: "POST",
        body: formData
      });
      const payload = await response.json();

      if (!response.ok || !payload.success) {
        throw new Error(payload.message || "Falha ao transcrever audio.");
      }

      setTranscription(payload);
      setStatus("Transcricao concluida. Extraindo campos estruturados.");
      fetchUsage();
      await extractFields(payload.transcriptionId, payload.transcript);
    } catch (requestError: any) {
      setError(requestError.message || "Falha ao chamar o endpoint de transcricao.");
      setStatus("Erro na transcricao.");
    } finally {
      setLoadingTranscription(false);
    }
  }

  async function extractFields(transcriptionId: string, transcript: string) {
    setLoadingExtraction(true);
    setError("");

    try {
      const response = await fetch(`${apiBaseUrl}/api/extract-form-fields`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          transcriptionId,
          transcript,
          requestedFields: reviewFields.map((field) => field.key)
        })
      });
      const payload = await response.json();

      if (!response.ok || !payload.success) {
        throw new Error(payload.message || payload.validationErrors?.join(" ") || "Falha ao extrair campos.");
      }

      setExtraction(payload);
      setFieldExtractions(payload.extraction.fields);
      setFormValues(reviewFields.reduce<Record<string, string>>((acc, field) => {
        acc[field.key] = payload.extraction.fields[field.key]?.value || "";
        return acc;
      }, {}));
      setStatus("Campos extraidos. Revise alertas e confirme antes de salvar.");
    } catch (requestError: any) {
      setError(requestError.message || "Falha ao extrair campos estruturados.");
      setStatus("Erro na extracao.");
    } finally {
      setLoadingExtraction(false);
    }
  }

  async function fetchUsage() {
    setLoadingUsage(true);

    try {
      const response = await fetch(`${apiBaseUrl}/api/usage`);
      const payload = await response.json();

      if (!response.ok || !payload.success) {
        throw new Error(payload.message || "Falha ao carregar uso.");
      }

      setUsage(payload);
    } catch (requestError: any) {
      setError(requestError.message || "Falha ao carregar uso estimado.");
    } finally {
      setLoadingUsage(false);
    }
  }

  function updateFormValue(key: string, value: string) {
    setFormValues((currentValues) => ({
      ...currentValues,
      [key]: value
    }));
    setReviewed(false);
    setConfirmed(false);
  }

  function applySingleFieldAudio(fieldKey: string, values: Record<string, string>, extractedFields: Record<string, ExtractedField>) {
    const extractedField = extractedFields[fieldKey];

    setFormValues((currentValues) => ({
      ...currentValues,
      [fieldKey]: values[fieldKey] || ""
    }));

    if (extractedField) {
      setFieldExtractions((currentFields) => ({
        ...currentFields,
        [fieldKey]: extractedField
      }));
    }

    setReviewed(false);
    setConfirmed(false);
    setStatus(`Campo ${reviewFields.find((field) => field.key === fieldKey)?.label || fieldKey} preenchido por audio. Revise antes de confirmar.`);
    fetchUsage();
  }

  function confirmReview() {
    setConfirmed(true);
    setStatus("Revisao confirmada. Nesta POC os dados nao sao persistidos no cadastro real.");
  }

  const busy = loadingTranscription || loadingExtraction;
  const hasFormValue = Object.values(formValues).some(Boolean);

  return (
    <>
      <Header step={ confirmed ? 3 : extraction ? 2 : 1 } total={ 3 } title="POC audio no cadastro" previous="#/" finished={ confirmed }></Header>

      <div className="chat-container audio-poc">
        <div className="chat">
          <div className="container">
            <div className="row">
              <div className="col-12 d-flex flex-column align-items-stretch justify-content-between">
                <div className="chat-item">
                  <img src="images/ico-chat-amarelinho.svg" alt="Amarelinho" />
                  <div className="chat-item-content">
                    <strong>Preenchimento por audio com Cloudflare Workers AI</strong>
                    <p>Envie ou grave um audio em portugues brasileiro. A POC transcreve, extrai campos, valida o JSON e exige revisao antes de confirmar.</p>
                    <span className="audio-poc-helper">Modelo: @cf/openai/whisper-large-v3-turbo, language=pt, task=transcribe, vad_filter=true, condition_on_previous_text=false.</span>
                  </div>
                </div>

                <div className="audio-poc-panel">
                  <div className="audio-poc-section-title">
                    <strong>O que este audio pode preencher</strong>
                    <span>{ reviewFields.length } campos</span>
                  </div>

                  <p className="audio-poc-browser-note">Grave uma fala natural com os dados que quiser testar. Campos ausentes ficam vazios ou marcados para revisão; nada é salvo sem confirmação.</p>

                  <div className="audio-poc-guide-grid">
                    { recordingGuideGroups.map((group) => (
                      <section className="audio-poc-guide-card" key={ group.title }>
                        <strong>{ group.title }</strong>
                        <div className="audio-poc-guide-fields">
                          { group.fields.map((field) => <span key={ field }>{ field }</span>) }
                        </div>
                        <p>{ group.example }</p>
                      </section>
                    )) }
                  </div>
                </div>

                <div className="audio-poc-panel">
                  <div className="audio-poc-upload">
                    <label className="btn btn-outline">
                      <i className="fa-solid fa-upload" aria-hidden="true"></i>
                      Escolher audio
                      <input type="file" accept="audio/*" onChange={ handleFileChange } />
                    </label>

                    <button className={ `btn btn-outline ${recording ? "active" : ""}` } type="button" onClick={ recording ? stopRecording : startRecording }>
                      <i className={ recording ? "fa-solid fa-stop" : "fa-solid fa-microphone" } aria-hidden="true"></i>
                      { recording ? "Parar gravacao" : "Gravar audio" }
                    </button>
                  </div>

                  { audioUrl &&
                    <div className="audio-poc-audio">
                      <audio controls src={ audioUrl } onLoadedMetadata={ (event) => setDurationSeconds(event.currentTarget.duration) }></audio>
                      <div className="audio-poc-meta">
                        <span>Arquivo: { audioFile?.name }</span>
                        <span>Duracao: { durationSeconds ? `${durationSeconds.toFixed(1)}s` : "indisponivel" }</span>
                        <span>Tamanho: { audioFile ? `${Math.round(audioFile.size / 1024)} KB` : "-" }</span>
                      </div>
                    </div>
                  }

                  <button className="btn btn-secondary audio-poc-primary-action" type="button" onClick={ transcribeAudio } disabled={ !audioFile || busy }>
                    { busy ? "Processando..." : "Transcrever e preencher formulario" }
                  </button>
                </div>

                { error &&
                  <div className="audio-poc-alert audio-poc-alert-error">
                    { error }
                  </div>
                }

                { transcription &&
                  <div className="audio-poc-panel">
                    <div className="audio-poc-section-title">
                      <strong>Transcricao completa</strong>
                      <span>{ transcription.transcriptionLatencyMs } ms</span>
                    </div>
                    <textarea className="audio-poc-transcript" value={ transcription.transcript } readOnly></textarea>
                    <div className="audio-poc-meta">
                      <span>ID: { transcription.transcriptionId }</span>
                      <span>Modelo: { transcription.model }</span>
                      <span>Minutos estimados: { formatNumber(transcription.usageEstimate.audioMinutes, 3) }</span>
                      <span>Neurônios estimados: { formatNumber(transcription.usageEstimate.estimatedNeurons, 2) }</span>
                      <span>Custo estimado: { formatCurrency(transcription.usageEstimate.estimatedUnitCostUsd) }</span>
                    </div>
                  </div>
                }

                <div className="audio-poc-panel">
                  <div className="audio-poc-section-title">
                    <strong>Uso e custo estimado da POC</strong>
                    <button className="btn btn-outline audio-poc-small-button" type="button" onClick={ fetchUsage } disabled={ loadingUsage }>
                      { loadingUsage ? "Atualizando..." : "Atualizar" }
                    </button>
                  </div>

                  { usage &&
                    <>
                      <div className="audio-poc-usage-grid">
                        <div>
                          <span>Hoje</span>
                          <strong>{ usage.summary.today.requests } chamada(s)</strong>
                          <p>{ formatNumber(usage.summary.today.audioMinutes, 3) } min | { formatNumber(usage.summary.today.estimatedNeurons, 2) } neurônios</p>
                        </div>
                        <div>
                          <span>Cota grátis restante</span>
                          <strong>{ formatNumber(usage.summary.today.estimatedFreeNeuronsRemaining, 2) }</strong>
                          <p>de { formatNumber(usage.summary.today.freeNeuronsPerDay, 0) } neurônios/dia</p>
                        </div>
                        <div>
                          <span>Custo unitário estimado</span>
                          <strong>{ formatCurrency(usage.summary.today.estimatedUnitCostUsd) }</strong>
                          <p>antes de considerar cota grátis</p>
                        </div>
                        <div>
                          <span>Pago estimado hoje</span>
                          <strong>{ formatCurrency(usage.summary.today.estimatedPaidOverageUsd) }</strong>
                          <p>apenas acima da cota grátis</p>
                        </div>
                      </div>

                      <p className="audio-poc-browser-note">{ usage.summary.note }</p>

                      { usage.events.length > 0 &&
                        <div className="audio-poc-usage-events">
                          { usage.events.slice(0, 5).map((event) => (
                            <div key={ event.id }>
                              <strong>{ event.fileName }</strong>
                              <span>{ new Date(event.createdAt).toLocaleString("pt-BR") }</span>
                              <span>{ formatNumber(event.usageEstimate.audioMinutes, 3) } min</span>
                              <span>{ formatCurrency(event.usageEstimate.estimatedUnitCostUsd) }</span>
                            </div>
                          )) }
                        </div>
                      }
                    </>
                  }

                  { !usage &&
                    <p className="audio-poc-empty">Nenhum uso carregado ainda. Transcreva um audio ou clique em atualizar.</p>
                  }
                </div>

                <div className="audio-poc-panel">
                  <div className="audio-poc-section-title">
                    <strong>Teste campo a campo</strong>
                    <span>um audio por campo</span>
                  </div>
                  <p className="audio-poc-browser-note">Clique no microfone ao lado do campo, fale apenas aquela informação e pare a gravação. O campo será preenchido com o que foi extraído para revisão.</p>
                </div>

                <div className="audio-poc-review" aria-label="Formulario para teste de preenchimento campo a campo por audio">
                  { reviewFields.map((field) => {
                    const extractedField = fieldExtractions[field.key];

                    return (
                      <label className={ getFieldClassName(extractedField) } key={ field.key }>
                        <span>{ field.label }</span>
                        <div className="field-with-audio audio-poc-field-with-audio">
                          { field.multiline &&
                            <textarea
                              value={ formValues[field.key] || "" }
                              placeholder={ field.placeholder }
                              onChange={ (event) => updateFormValue(field.key, event.target.value) }
                            />
                          }
                          { !field.multiline &&
                            <input
                              value={ formValues[field.key] || "" }
                              placeholder={ field.placeholder }
                              onChange={ (event) => updateFormValue(field.key, event.target.value) }
                            />
                          }
                          <AudioFormFillControl
                            fields={ [{ key: field.key, label: field.label }] }
                            buttonTitle={ `Preencher ${field.label} por áudio` }
                            onApply={ (values, extractedFields) => applySingleFieldAudio(field.key, values, extractedFields) }
                          />
                        </div>
                        <small>
                          { extractedField?.status || "missing" } / { extractedField?.confidence || "low" }
                          { extractedField?.evidence ? ` - evidencia: ${extractedField.evidence}` : "" }
                        </small>
                      </label>
                    );
                  }) }
                </div>

                <div className="audio-poc-panel">
                  <label className="audio-poc-review-check">
                    <input type="checkbox" checked={ reviewed } onChange={ (event) => setReviewed(event.target.checked) } />
                    <span>Revisei a transcricao, os campos e os alertas antes de confirmar.</span>
                  </label>
                  <button className="btn btn-secondary audio-poc-primary-action" type="button" onClick={ confirmReview } disabled={ !reviewed || !hasFormValue }>
                    Confirmar revisao
                  </button>
                  { confirmed &&
                    <p className="audio-poc-success">Confirmado na POC. Nenhum dado foi salvo no cadastro real.</p>
                  }
                </div>

                { extraction &&
                  <>
                    <div className="audio-poc-panel">
                      <div className="audio-poc-section-title">
                        <strong>Alertas de revisao</strong>
                        <span>{ extraction.extraction.alerts.length } alerta(s)</span>
                      </div>
                      { extraction.extraction.alerts.length > 0 &&
                        <ul className="audio-poc-alert-list">
                          { extraction.extraction.alerts.map((alert) => <li key={ alert }>{ alert }</li>) }
                        </ul>
                      }
                      { extraction.extraction.alerts.length === 0 &&
                        <p className="audio-poc-empty">Nenhum alerta automatico. Ainda assim revise antes de confirmar.</p>
                      }
                    </div>
                  </>
                }
              </div>
            </div>
          </div>
        </div>

        <footer className="footer-answer audio-poc-footer">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <div className="audio-poc-status">
                  <span className={ recording || busy ? "audio-poc-recording" : "" }></span>
                  <p>{ status }</p>
                </div>
                <p className="audio-poc-browser-note">API de transcricao em { apiBaseUrl || "mesma origem" }. Local: suba o Worker com npm run poc:api.</p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
